import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useCart } from './CartContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebase';

// 创建Context
const PaymentContext = createContext();

// 使用Hook
export const usePayment = () => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error('usePayment must be used within a PaymentProvider');
  }
  return context;
};

// Provider组件
export const PaymentProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const { items, totalAmount } = useCart();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paypalOrderId, setPaypalOrderId] = useState(null);
  const [orderStatus, setOrderStatus] = useState(null);

  // 获取Firebase Functions实例
  const functions = getFunctions(getApp(), 'asia-southeast1');
  
  console.log('PaymentContext - 初始化Firebase Functions');
  console.log('PaymentContext - 当前登录状态:', currentUser ? '已登录' : '未登录');

  // 清除错误
  const clearError = () => {
    setError(null);
  };

  // 获取用户的收货信息
  const getShippingInfo = async () => {
    if (!currentUser) {
      throw new Error('用户未登录');
    }

    const shippingRef = doc(db, 'users', currentUser.uid, 'shipping', 'default');
    const shippingDoc = await getDoc(shippingRef);

    if (!shippingDoc.exists()) {
      throw new Error('请先填写收货信息');
    }

    const shippingInfo = shippingDoc.data();
    const requiredFields = ['fullName', 'address', 'city', 'state', 'zipCode', 'country', 'phone'];
    const missingFields = requiredFields.filter(field => !shippingInfo[field]);

    if (missingFields.length > 0) {
      throw new Error('收货信息不完整，请补充完整');
    }

    return shippingInfo;
  };

  // 创建 PayPal 订单
  const createPaypalOrder = async (shippingCost = 0) => {
    console.log('开始创建 PayPal 订单...');
    try {
      const shippingInfo = await getShippingInfo();
      const orderTotal = totalAmount + Number(shippingCost);

      // 构建订单数据
      const orderData = {
        purchase_units: [
          {
            amount: {
              value: orderTotal.toFixed(2),
              currency_code: 'SGD' // 改为新加坡元
            },
            shipping: {
              name: {
                full_name: shippingInfo.fullName
              },
              address: {
                address_line_1: shippingInfo.address,
                admin_area_2: shippingInfo.city,
                admin_area_1: shippingInfo.state,
                postal_code: shippingInfo.zipCode,
                country_code: shippingInfo.country
              }
            }
          }
        ]
      };

      console.log('订单数据:', orderData);
      return { orderId: orderData };
    } catch (error) {
      console.error('创建订单失败:', error);
      throw error;
    }
  };

  // 捕获 PayPal 订单
  const capturePaypalOrder = async (orderId) => {
    console.log('开始捕获 PayPal 订单:', orderId);
    try {
      // 这里可以添加与后端的通信逻辑
      return {
        status: 'COMPLETED',
        orderId: orderId
      };
    } catch (error) {
      console.error('捕获订单失败:', error);
      throw error;
    }
  };

  // 验证PayPal配置 - 使用Firebase Callable函数
  const verifyPaypalConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // 调用Firebase Callable函数验证PayPal配置
      console.log('正在调用verifyPaypalConfig云函数...');
      const verifyPaypalConfigFn = httpsCallable(functions, 'verifyPaypalConfig');
      const result = await verifyPaypalConfigFn();
      
      console.log('验证PayPal配置响应:', result.data);
      
      return result.data;
    } catch (error) {
      console.error('验证PayPal配置失败:', error);
      setError(error.message || '验证PayPal配置失败');
      return null;
    } finally {
      setLoading(false);
    }
  };

  // 获取PayPal客户端ID - 使用Firebase Callable函数
  const getPaypalClientId = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // 调用Firebase Callable函数获取PayPal客户端ID
      console.log('正在调用getPaypalClientId云函数...');
      const getPaypalClientIdFn = httpsCallable(functions, 'getPaypalClientId');
      const result = await getPaypalClientIdFn();
      
      console.log('获取PayPal客户端ID响应:', result.data);
      
      return result.data;
    } catch (error) {
      console.error('获取PayPal客户端ID失败:', error);
      setError(error.message || '获取PayPal客户端ID失败');
      return null;
    } finally {
      setLoading(false);
    }
  };

  // 提供Context值
  const value = {
    loading,
    error,
    clearError,
    createPaypalOrder,
    capturePaypalOrder,
    verifyPaypalConfig,
    getPaypalClientId,
    paypalOrderId,
    orderStatus
  };

  return (
    <PaymentContext.Provider value={value}>
      {children}
    </PaymentContext.Provider>
  );
};

export default PaymentProvider;